import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { whatsappProvisioningService } from "@/services/whatsapp-provisioning-service";
import {
  SyncWhatsAppSystemFlowsRequest,
  SyncWhatsAppSystemFlowsResponse,
  WhatsAppBotReadinessStatusResponse,
  WhatsAppFlowOperationsStatus,
} from "@/types/communication-integration";
import {
  UpdateWhatsAppHotelConfigRequest,
  WhatsAppHotelConfig,
  WhatsAppLifecycleEvent,
  WhatsAppTemplate,
  WhatsAppTemplateBootstrapResult,
  WhatsAppTemplateBinding,
  WhatsAppTemplateBlueprint,
} from "@/types/whatsapp";
import { whatsappKeys } from "@/hooks/whatsapp.hooks";

export const whatsappProvisioningKeys = {
  all: ["whatsappProvisioning"] as const,
  flowStatus: (hotelId: string) =>
    [...whatsappProvisioningKeys.all, "flowStatus", hotelId] as const,
  botReadiness: (hotelId: string) =>
    [...whatsappProvisioningKeys.all, "botReadiness", hotelId] as const,
  hotelConfig: (hotelId: string) =>
    [...whatsappProvisioningKeys.all, "hotelConfig", hotelId] as const,
  templateBlueprints: (hotelId: string, language: string) =>
    [
      ...whatsappProvisioningKeys.all,
      "templateBlueprints",
      hotelId,
      language,
    ] as const,
  templates: (hotelId: string) =>
    [...whatsappProvisioningKeys.all, "templates", hotelId] as const,
  templateBindings: (hotelId: string) =>
    [...whatsappProvisioningKeys.all, "templateBindings", hotelId] as const,
};

export const useWhatsAppFlowOperationsStatus = (
  hotelId: string,
  enabled: boolean,
) =>
  useQuery<WhatsAppFlowOperationsStatus>({
    queryKey: whatsappProvisioningKeys.flowStatus(hotelId),
    queryFn: () => whatsappProvisioningService.getFlowOperationsStatus(hotelId),
    enabled,
    staleTime: 30_000,
  });

export const useWhatsAppBotReadinessStatus = (
  hotelId: string,
  enabled: boolean,
) =>
  useQuery<WhatsAppBotReadinessStatusResponse>({
    queryKey: whatsappProvisioningKeys.botReadiness(hotelId),
    queryFn: () => whatsappProvisioningService.getBotReadinessStatus(hotelId),
    enabled,
    staleTime: 30_000,
  });

export const useWhatsAppHotelConfig = (hotelId: string, enabled: boolean) =>
  useQuery<WhatsAppHotelConfig>({
    queryKey: whatsappProvisioningKeys.hotelConfig(hotelId),
    queryFn: () => whatsappProvisioningService.getHotelConfig(hotelId),
    enabled,
    retry: false,
    staleTime: 30_000,
  });

export const useUpdateWhatsAppHotelConfig = (hotelId: string) => {
  const qc = useQueryClient();
  return useMutation<
    WhatsAppHotelConfig,
    unknown,
    UpdateWhatsAppHotelConfigRequest
  >({
    mutationFn: (payload) =>
      whatsappProvisioningService.updateHotelConfig(hotelId, payload),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: whatsappProvisioningKeys.hotelConfig(hotelId),
      });
      qc.invalidateQueries({
        queryKey: whatsappProvisioningKeys.botReadiness(hotelId),
      });
    },
  });
};

export const useSyncWhatsAppSystemFlows = (hotelId: string) => {
  const qc = useQueryClient();
  return useMutation<
    SyncWhatsAppSystemFlowsResponse,
    unknown,
    SyncWhatsAppSystemFlowsRequest
  >({
    mutationFn: (payload) =>
      whatsappProvisioningService.syncSystemFlows(hotelId, payload),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: whatsappProvisioningKeys.flowStatus(hotelId),
      });
      qc.invalidateQueries({
        queryKey: whatsappProvisioningKeys.botReadiness(hotelId),
      });
    },
  });
};

export const useWhatsAppTemplateBlueprints = (
  hotelId: string,
  enabled: boolean,
  language = "en_US",
) =>
  useQuery<WhatsAppTemplateBlueprint[]>({
    queryKey: whatsappProvisioningKeys.templateBlueprints(hotelId, language),
    queryFn: () =>
      whatsappProvisioningService.listLifecycleTemplateBlueprints(
        hotelId,
        language,
      ),
    enabled,
    staleTime: 60_000,
  });

export const useWhatsAppTemplates = (hotelId: string, enabled: boolean) =>
  useQuery<WhatsAppTemplate[]>({
    queryKey: whatsappProvisioningKeys.templates(hotelId),
    queryFn: () => whatsappProvisioningService.listTemplates(hotelId),
    enabled,
    staleTime: 30_000,
  });

export const useBootstrapWhatsAppTemplates = (hotelId: string) => {
  const qc = useQueryClient();
  return useMutation<WhatsAppTemplateBootstrapResult, unknown, void>({
    mutationFn: () =>
      whatsappProvisioningService.bootstrapLifecycleTemplates(hotelId),
    onSuccess: () => {
      invalidateTemplateOperations(qc, hotelId);
      qc.invalidateQueries({
        queryKey: whatsappProvisioningKeys.botReadiness(hotelId),
      });
      qc.invalidateQueries({
        queryKey: whatsappProvisioningKeys.templateBlueprints(hotelId, "en_US"),
      });
    },
  });
};

export const useWhatsAppTemplateBindings = (
  hotelId: string,
  enabled: boolean,
) =>
  useQuery<WhatsAppTemplateBinding[]>({
    queryKey: whatsappProvisioningKeys.templateBindings(hotelId),
    queryFn: () => whatsappProvisioningService.listTemplateBindings(hotelId),
    enabled,
    staleTime: 30_000,
  });

export const useCreateWhatsAppTemplateDraft = (hotelId: string) => {
  const qc = useQueryClient();
  return useMutation<WhatsAppTemplate, unknown, WhatsAppLifecycleEvent>({
    mutationFn: (event) =>
      whatsappProvisioningService.createTemplateDraftFromBlueprint(
        hotelId,
        event,
      ),
    onSuccess: () => {
      invalidateTemplateOperations(qc, hotelId);
    },
  });
};

export const useSubmitWhatsAppTemplateDraft = (hotelId: string) => {
  const qc = useQueryClient();
  return useMutation<WhatsAppTemplate, unknown, string>({
    mutationFn: (templateId) =>
      whatsappProvisioningService.submitTemplateDraft(hotelId, templateId),
    onSuccess: () => {
      invalidateTemplateOperations(qc, hotelId);
    },
  });
};

export const useBindWhatsAppRecommendedTemplate = (hotelId: string) => {
  const qc = useQueryClient();
  return useMutation<WhatsAppTemplateBinding, unknown, string>({
    mutationFn: (templateId) =>
      whatsappProvisioningService.bindTemplateFromRecommendedMetadata(
        hotelId,
        templateId,
      ),
    onSuccess: () => {
      invalidateTemplateOperations(qc, hotelId);
      qc.invalidateQueries({
        queryKey: whatsappProvisioningKeys.botReadiness(hotelId),
      });
    },
  });
};

const invalidateTemplateOperations = (
  qc: ReturnType<typeof useQueryClient>,
  hotelId: string,
) => {
  qc.invalidateQueries({
    queryKey: whatsappProvisioningKeys.templates(hotelId),
  });
  qc.invalidateQueries({
    queryKey: whatsappProvisioningKeys.templateBindings(hotelId),
  });
  qc.invalidateQueries({
    queryKey: whatsappKeys.templates(hotelId),
  });
};
